import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';

export type SimplifiedOptionPremiumModel = 'BLACK_SCHOLES' | 'APPROXIMATION';

export type SimplifiedOptionPremiumModelResponse = {
  session_id: number;
  simplified_option_premium_model: SimplifiedOptionPremiumModel;
};

export const SIMPLIFIED_OPTION_PREMIUM_MODEL_OPTIONS = [
  { value: 'BLACK_SCHOLES' as const, label: 'Black-Scholes' },
  { value: 'APPROXIMATION' as const, label: 'Approximation' },
];

/** Convert PM API error payloads into a concise message for the admin card. */
const apiErrorMessage = (result: unknown): string => {
  if (typeof result === 'string') return result;
  if (result && typeof result === 'object') {
    const messages = Object.values(result as Record<string, unknown>).flatMap(
      value => (Array.isArray(value) ? value : [value]),
    );
    if (messages.length) return messages.map(String).join(' ');
  }
  return 'The premium model request failed.';
};

/** Fetch the isolated admin setting without adding it to shared session payloads. */
export const fetchSimplifiedOptionPremiumModel = async (
  sessionId: number,
): Promise<SimplifiedOptionPremiumModelResponse> => {
  const { result, status } = await apiCaller<{ session_id: number }>({
    path: '/admin/fetch_simplified_option_premium_model',
    method: 'GET',
    data: { session_id: sessionId },
    apiUrl: API_URL,
  });

  if (status.toString()[0] !== '2') {
    throw new Error(apiErrorMessage(result));
  }
  return result as SimplifiedOptionPremiumModelResponse;
};

/** Persist the explicit admin choice through the enum-restricted backend endpoint. */
export const updateSimplifiedOptionPremiumModel = async (
  sessionId: number,
  premiumModel: SimplifiedOptionPremiumModel,
): Promise<SimplifiedOptionPremiumModelResponse> => {
  const { result, status } = await apiCaller<{
    session_id: number;
    simplified_option_premium_model: SimplifiedOptionPremiumModel;
  }>({
    path: '/admin/update_simplified_option_premium_model',
    method: 'POST',
    data: {
      session_id: sessionId,
      simplified_option_premium_model: premiumModel,
    },
    apiUrl: API_URL,
  });

  if (status.toString()[0] !== '2') {
    throw new Error(apiErrorMessage(result));
  }
  return result as SimplifiedOptionPremiumModelResponse;
};
